import { useAppSelector } from '@app/store/hooks';
import { Lock } from '@mui/icons-material';
import { Box, Button, Typography } from '@mui/material';
import { Navigate, Outlet, useNavigate } from 'react-router-dom';

/**
 * Role Level Constants - must match backend
 */
export const ROLE_LEVELS = {
	SUPER_ADMIN: 100,
	MANAGER: 75,
	OPERATOR: 50,
	PICKER: 25,
} as const;

interface RequireLevelProps {
	minLevel: number;
	children?: React.ReactNode;
}

/**
 * Route protection component based on user's role level
 * Shows access denied message if user doesn't have required level
 */
export function RequireLevel({ minLevel, children }: RequireLevelProps) {
	const navigate = useNavigate();
	const { user, isAuthenticated } = useAppSelector((state) => state.auth);

	// If not authenticated, redirect to login
	if (!isAuthenticated || !user) {
		return <Navigate to='/login' replace />;
	}

	const userLevel = user.roleLevel || 0;

	// If user doesn't have required level, show access denied
	if (userLevel < minLevel) {
		return (
			<Box
				sx={{
					display: 'flex',
					flexDirection: 'column',
					alignItems: 'center',
					justifyContent: 'center',
					minHeight: '60vh',
					gap: 2,
					p: 4,
				}}>
				<Lock sx={{ fontSize: 64, color: 'error.main' }} />
				<Typography variant='h4' fontWeight={600}>
					Достъпът е отказан
				</Typography>
				<Typography color='text.secondary' textAlign='center'>
					Нямате достатъчно права за достъп до тази страница.
					<br />
					Изисква се ниво {minLevel}, вашето ниво е {userLevel}.
				</Typography>
				<Button variant='contained' onClick={() => navigate('/')} sx={{ mt: 2 }}>
					Към началната страница
				</Button>
			</Box>
		);
	}

	return children ? <>{children}</> : <Outlet />;
}

/**
 * Hook to check if current user has required level
 */
export function useHasLevel(minLevel: number): boolean {
	const { user } = useAppSelector((state) => state.auth);
	return (user?.roleLevel || 0) >= minLevel;
}

/**
 * Hook to get current user's role level
 */
export function useRoleLevel(): number {
	const { user } = useAppSelector((state) => state.auth);
	return user?.roleLevel || 0;
}

/**
 * Convenience hooks for common access levels
 */
export function useIsSuperAdmin(): boolean {
	return useHasLevel(ROLE_LEVELS.SUPER_ADMIN);
}

export function useIsManager(): boolean {
	return useHasLevel(ROLE_LEVELS.MANAGER);
}

export function useIsOperator(): boolean {
	return useHasLevel(ROLE_LEVELS.OPERATOR);
}

/**
 * Check if user has write access (level 75+)
 * Level 50 users are read-only
 */
export function useCanWrite(): boolean {
	return useHasLevel(ROLE_LEVELS.MANAGER);
}

/**
 * Check if user is read-only (level 50)
 */
export function useIsReadOnly(): boolean {
	const level = useRoleLevel();
	return level >= ROLE_LEVELS.OPERATOR && level < ROLE_LEVELS.MANAGER;
}

/**
 * Check if user has global warehouse access (level 100)
 */
export function useHasGlobalAccess(): boolean {
	return useHasLevel(ROLE_LEVELS.SUPER_ADMIN);
}
